<script setup lang="ts">
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

import { Ellipsis, Pencil, Users, Trash2, SplitSquareHorizontal, PanelLeft, PanelRight, PanelTop, PanelBottom } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
const store = useStore()
const router = useRouter()
const selectedOrgId = computed(() => store.getters['org/getSelected'])

const props = defineProps<{
  map: any
}>()

const maps = computed(() => store.getters['maps/getMaps'](selectedOrgId.value))
const tiles = computed(() => store.getters['split/getTiles'](selectedOrgId.value))

// Directional tiling: placing this map at an edge makes a 2-up (this map at the
// edge, the currently-shown map opposite); a 3rd/4th map auto-fills a quad.
const DIRECTIONS = [
  { dir: 'left', label: 'Left', icon: PanelLeft },
  { dir: 'right', label: 'Right', icon: PanelRight },
  { dir: 'top', label: 'Top', icon: PanelTop },
  { dir: 'bottom', label: 'Bottom', icon: PanelBottom },
]
const arrangement = computed(() => store.getters['split/getArrangement'](selectedOrgId.value))
const inSplit = computed(() => tiles.value.includes(props.map.id))

// This map's current edge (2-up, or the master edge in a 3-up) — greyed out.
const currentDirection = computed(() => {
  const t = tiles.value
  const idx = t.indexOf(props.map.id)
  if (idx === -1) return null
  const a = arrangement.value
  if (t.length === 2) {
    if (a === 'tb') return idx === 0 ? 'top' : 'bottom'
    return idx === 0 ? 'left' : 'right'
  }
  if (t.length === 3 && ['left', 'right', 'top', 'bottom'].includes(a)) {
    const masterIdx = (a === 'left' || a === 'top') ? 0 : 2
    return idx === masterIdx ? a : null
  }
  return null
})
const place = (direction: string) => {
  store.dispatch('split/placeInSplit', { orgId: selectedOrgId.value, mapId: props.map.id, direction })
}
const removeFromSplit = () => {
  store.dispatch('split/removeTileMap', { orgId: selectedOrgId.value, mapId: props.map.id })
}

const showDeleteConfirm = ref(false)

const edit = () => {
  router.push({ name: 'MapEdit', params: { org_id: selectedOrgId.value, map_id: props.map.id } })
}

const goToMembers = () => {
  router.push({ name: 'MapMembers', params: { org_id: selectedOrgId.value, map_id: props.map.id } })
}

const confirmDelete = () => {
  store.dispatch('maps/deleteMap', { orgId: selectedOrgId.value, mapId: props.map.id })
  showDeleteConfirm.value = false
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button size="sm" variant="ghost" class="w-6 h-6 p-0 rounded-full" @click.stop>
        <Ellipsis class="w-4 h-4 text-muted-foreground" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" class="w-48">
      <DropdownMenuGroup>
        <DropdownMenuItem class="cursor-pointer" @click="edit">
          <Pencil class="w-4 h-4 mr-2" />
          <span>Settings</span>
        </DropdownMenuItem>
        <DropdownMenuItem class="cursor-pointer" @click="goToMembers">
          <Users class="w-4 h-4 mr-2" />
          <span>Members</span>
        </DropdownMenuItem>
        <DropdownMenuSub v-if="maps.length > 1">
          <DropdownMenuSubTrigger class="cursor-pointer">
            <SplitSquareHorizontal class="w-4 h-4 mr-2" />
            <span>Split</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem
              v-for="d in DIRECTIONS"
              :key="d.dir"
              class="cursor-pointer"
              :disabled="d.dir === currentDirection"
              @click="place(d.dir)"
            >
              <component :is="d.icon" class="w-4 h-4 mr-2" />
              <span>{{ d.label }}</span>
            </DropdownMenuItem>
            <template v-if="inSplit">
              <DropdownMenuSeparator />
              <DropdownMenuItem class="cursor-pointer" @click="removeFromSplit">
                <span>Remove from split</span>
              </DropdownMenuItem>
            </template>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuGroup>
      <template v-if="!map.isDefault">
        <DropdownMenuSeparator />
        <DropdownMenuItem class="text-red-500 cursor-pointer" @click="showDeleteConfirm = true">
          <Trash2 class="w-4 h-4 mr-2" />
          <span>Delete</span>
        </DropdownMenuItem>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>

  <Dialog v-model:open="showDeleteConfirm">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Delete map</DialogTitle>
        <DialogDescription>
          Delete “{{ map.name }}”? Its layers will move back to the Default Map. This can’t be undone.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" @click="showDeleteConfirm = false">Cancel</Button>
        <Button variant="destructive" @click="confirmDelete">Delete</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
